/**
 * Layout principal para área Admin Root
 * Inclui sidebar, header e seletor de idioma
 */
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { LogOut, Eye } from 'lucide-react';
import { ThemeToggle } from '@/components/ui/theme-toggle';

import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

interface AdminLayoutProps {
  children: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { user, signOut, isRootAdmin } = useAuth();
  const { t } = useTranslation('sidebar');

  /**
   * Executa logout com redirecionamento via window.location
   */
  const handleLogout = () => {
    signOut(() => {
      window.location.href = '/auth';
    });
  };

  return (
    <TooltipProvider>
      <SidebarProvider>
        <div className="app-viewport-min flex w-full">
          <AdminSidebar />
          {/* 
           * Container do conteúdo com min-w-0 para evitar overflow em flex layouts
           * relative z-0 cria stacking context - garante que sidebar (z-10) fique acima
           */}
          <div className="flex-1 min-w-0 relative z-0 flex flex-col">
            <header className="h-14 border-b bg-background flex items-center justify-between px-4">
              <div className="flex items-center gap-2">
                <SidebarTrigger />
                <span className="font-semibold text-foreground">UaizyChat Admin</span>
              </div>
              <div className="flex items-center gap-2">

                {/* Acesso observador ao painel White Label (apenas root_admin) */}
                {isRootAdmin && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/wl">
                          <Eye className="h-4 w-4 mr-2" />
                          Painel WL
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>Acessar painel White Label como observador (somente leitura)</p>
                    </TooltipContent>
                  </Tooltip>
                )}

                {/* Toggle de Tema */}
                <ThemeToggle />

                <span className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
                <span className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
                
                {/* Botão de Logout */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleLogout}>
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>{t('tooltips.logout')}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </header>
            {/* 
             * Main com min-w-0 e overflow-x-hidden para conter qualquer conteúdo largo
             * Evita que tabelas ou grids expandam o layout inteiro
             */}
            <main className="flex-1 min-w-0 overflow-x-hidden p-6 bg-muted/30">
              {children}
            </main>
          </div>
        </div>
      </SidebarProvider>
    </TooltipProvider>
  );
}
